import { Component, OnDestroy, OnInit } from "@angular/core";
import { ConfirmationService, MenuItem } from "primeng/api";
import { SubSink } from "subsink";
import { ITrainingCourse } from "../shared/models/training-course";
import { TrainingCourseService } from "../services/training-course.service";
import { NgxSpinnerService } from "ngx-spinner";
import { ToastService } from "../shared/toast/toast.service";
import { PpsUtilityService } from "../services/pps-utility.service";
import { BreakpointObserver, BreakpointState } from "@angular/cdk/layout";
import { DeleteActionComponent } from "./delete-action.component";
import * as _ from "lodash";

@Component({
  selector: "pps-manage-training-course",
  templateUrl: "./manage-training-course.component.html"
})
export class ManageTrainingCourseComponent implements OnInit, OnDestroy{
  private subs = new SubSink();

  smallScreen: boolean = false;
  spinnerName: string = "dataSpinner";

  menuItems: MenuItem[] = [
    {label: "Settings", icon: "pi-fw fas fa-tools"}, 
    {label: "Manage Courses", icon: "pi-fw fas fa-graduation-cap"},
  ];

  oldItems : ITrainingCourse[] = [];
  items: ITrainingCourse[] = [];

  gridHeaders: any[];
  context:any = {};
  entityLoading: boolean = false;
  gridOptions: any = {
    getRowId: param => param.data.id,
    defaultColDef:{
      enableCellChangeFlash: true,
      resizable: true
    }
  };

  private gridApi;

  constructor(private _trainingCourseService: TrainingCourseService,
              private _spinner: NgxSpinnerService,
              private _toast: ToastService,
              private _confirm: ConfirmationService,
              private _util: PpsUtilityService,
              private breakpointObserver: BreakpointObserver,) { 
    this.subs.sink = this.breakpointObserver.observe(['(min-width: 768px)'])
      .subscribe((state: BreakpointState) => {
        this.smallScreen = !state.matches;
      });       
  }
  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.initialize();
  }

  onGridReady(params) {
    this.gridApi = params.api;
  }    

  loadData(){
    this._spinner.show(this.spinnerName);
    this.entityLoading = true;
    this.items.length = 0;
    this.subs.sink = this._trainingCourseService.getAll()
      .subscribe(apiResponse => {
        if (apiResponse.succeed){
          this.items = apiResponse.result;
          this.oldItems = _.cloneDeep(this.items);
          this._toast.succeeded(`${this.items.length} records loaded`);
        }else{
          this._toast.failed(`Failed to load course data: ${apiResponse.errors.join(";")}`);
        }
      }, _error => {
        console.log(_error);
      })
      .add(()=>{
        this._spinner.hide(this.spinnerName);
        this.entityLoading = false;
      });
  }

  addTrainingCourse(){
    let item: ITrainingCourse = {
      id: 0,
      title: "Course title",
      description: "Course description",
      active: true
    };

    this._spinner.show(this.spinnerName);

    this.subs.sink = this._trainingCourseService.add(item)
      .subscribe(res => {
        if (res.succeed){
          this.items.unshift(res.result);
          this.oldItems = _.cloneDeep(this.items);
          this.gridApi.setRowData(this.items);
          let rowNode = this.gridApi.getRowNode(res.result.id);
          if (rowNode){
            this.gridApi.flashCells({rowNodes: [rowNode]});
            rowNode.setSelected(true);
            window.setTimeout( () =>{
              this.gridApi.startEditingCell({rowIndex: rowNode.rowIndex, colKey: "title"});
            }, 500);
          }
        }
      }, _error => {
        console.log(_error);
      })
      .add(()=>{
        this._spinner.hide(this.spinnerName);
        this.entityLoading = false;
      });
  }

  delete(item: ITrainingCourse){
    if (item === null){
      return;
    }

    this._confirm.confirm({
      header: `Delete Course`,
      message: `Are you sure that you want to delete [${item.title}]?`,
      icon: "fas fa-exclamation-circle icon-danger",
      defaultFocus: "accept",
      accept: () => this.deleteHelper(item)
    });
  }

  deleteHelper(item: ITrainingCourse){
    item.active = false;
    this.subs.sink = this._trainingCourseService.update(item)
      .subscribe(apiResponse => {
        if (apiResponse.succeed){
          var foundIndex = this.items.findIndex(x => x.id == item.id);
          if (foundIndex !== -1 ){
            this.items.splice(foundIndex, 1);
            this.oldItems = _.cloneDeep(this.items);
            this.gridApi.setRowData(this.items);
            this._toast.succeeded(`[${item.title}] deleted!`);
          }else{
            this._toast.failed(`Delete [${item.title}] failed!`);
          }
        }else{
          this._toast.failed(`Failed to delete [${item.title}]: ${apiResponse.errors.join("|")}`);
        }
      });
  }

  private getActionComponentTooltip(trainingCourse: ITrainingCourse):string{
    return "Click to delete training course: " + trainingCourse.title;
  }

  private initialize(){
    this.context = { componentParent: this };
    this.gridHeaders = this.getColumnDefs();
    this.loadData();
  }

  private getColumnDefs(){
    let cols = [
      {
        headerName: "",
        field: "action",
        cellRenderer: DeleteActionComponent,
        resizable: false,
        width: 50,
        hide: false,
        filter: false,        
        suppressMovable: true,
        cellStyle: {textAlign: "center"}
      },
      {
        headerName: 'Course Title',
        field:'title',
        filter: true,
        floatingFilter: true,
        width: 500,
        editable: true,
        sortable: true,
        //comparator: (date1, date2) => this._util.dateComparator(date1, date2),
        onCellValueChanged: this.dataChanged,
        cellStyle: {textAlign: "left"}
      },
      {
        headerName: 'Course Description',
        field:'description',
        filter: true,
        floatingFilter: true,
        flex: 1,
        editable: true,
        sortable: true,
        onCellValueChanged: this.dataChanged,
        headerClass: "ag-ext-header-align-left"
      }
    ];

    return cols;
  }

  private dataChanged(params){
    let _this = params.context.componentParent;
    let course = params.data;

    _this.update(course);
  }

  private update(course: ITrainingCourse){
    this._spinner.show(this.spinnerName);
    
    this.subs.sink = this._trainingCourseService.update(course)
      .subscribe(res => {
        if (res.succeed){
          this._toast.succeeded(`Course updated`);
          course.title = res.result.title;
          course.description = res.result.description;
          //this.oldHolidays = _.cloneDeep(this.holidays);
        }else{
          this._toast.failed(`Courseupdate failed: ${res.errors.join("|")}`);
        }
      })
      .add(() => this._spinner.hide(this.spinnerName));
  }
}  
